import { ChevronLeft, FileText, ChevronDown } from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import styled, {keyframes} from 'styled-components';
import { executeWorkflowAsync, fetchClaimDetails } from '../services/workbenchDataService';
import { useToast } from '../components/common/ToastProvider';
import { getEntityByFilter, updateEntityListItem } from '../services/entityService';
import { getUserData } from '../utils/authUtils';
import { useNavigate } from 'react-router-dom';
import { ClaimFetchModal, ClaimSubmitModal } from '../components/common/ClaimModal';



const PageWrapper = styled.div`
  display: flex;
  width: 100%;
  min-height: 100vh;
  background: #f5f7fa;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
    sans-serif;
`;

const MainContent = styled.div`
  flex: 1;
  padding: 1.5rem 3rem;
  max-width: 100%;
  background: #F3F5F7;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.2rem;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  padding: 10px;
  border-radius: 50%;
  background: #e5f5ff;
  font-size: 1.5rem;
  cursor: pointer;
  color: #0088cc;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  
  &:hover {
    opacity: 0.7;
  }
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 500;
  color: #404040;
  margin: 0;
  letter-spacing: -0.5px;
`;

const TopCard = styled.div`
  background: #233A67;
  border-radius: 16px;
  padding: 1rem 1.5rem;
  margin-bottom: 1.5rem;
  display: flex;
  justify-content:space-between;
  gap: 1.5rem;
  height: 100px;
  align-items: center;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);

  @media (max-width: 1400px) {
    gap: 1rem;
    padding: 1.5rem;
    grid-template-columns: auto 1fr auto auto auto auto;
  }

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const IconWrapper = styled.div`
  width: 50px;
  height: 50px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.7);
  flex-shrink: 0;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 150px;
`;

const Label = styled.label`
  font-size: 0.75rem;
  color: #94A6CA;
  font-weight: 500;
//   text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Input = styled.input`
  padding: 0.6rem 0.8rem;
  border: none;
  border-radius: 6px;
  background: white;
  font-size: 0.9rem;
  font-weight: 600;
  color: #1a1a1a;
  height:36px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:focus {
    outline: none;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &::placeholder {
    color: #999;
  }
`;

const FetchButton = styled.button`
  border: 2px solid #00a8ff;
  background: transparent;
  color: #00a8ff;
  border-radius: 50px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  letter-spacing: 0.3px;
  white-space: nowrap;
  width: 160px;
  height: 36px;
  margin-top:1.4rem;

  &:hover {
    background: rgba(0, 168, 255, 0.1);
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const InfoColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  min-width: auto;
`;

const InfoLabel = styled.span`
  font-size: 0.75rem;
  color: #94A6CA;
//   text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
`;

const InfoValue = styled.span`
  font-size: 1rem;
  color: white;
  font-weight: 500;
//   letter-spacing: -0.3px;
`;

const ProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  min-width: 100px;
  margin-top:-5px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 5px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
  overflow: hidden;

  &::after {
    content: '';
    display: block;
    width: ${props => props.progress || 80}%;
    height: 100%;
    background: linear-gradient(90deg, #00a8ff 0%, #00c8ff 100%);
    border-radius: 3px;
    transition: width 0.3s ease;
  }
`;

const SubmitButton = styled.button`
  padding: 0.7rem 1.5rem;
  background: #00a1ff;
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  letter-spacing: 0.3px;
  white-space: nowrap;
  height: fit-content;
  width: 110px;

  &:hover {
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SaveButton = styled.button`
  padding: 0.7rem 1.5rem;
  background: #00A1FF33;
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  width: 110px;
  transition: all 0.3s ease;
  letter-spacing: 0.3px;
  white-space: nowrap;
  height: fit-content;

  &:hover {
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const TopContentWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
 
`;
 
// Container for icon, input, and fetch button
const InputSection = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;

`;
 
// Container for info columns
const InfoSection = styled.div`
  display: flex;
  gap: 3rem;
 
`;
 
// Container for buttons
const ButtonSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

`;

const MainTabContainerHead = styled.div`
    background: #fff;
    padding: 26px 26px;
    border-radius:10px;
`;

const TabsContainer = styled.div`
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 2rem;
  background: white;
  border-radius: 8px 8px 0 0;
`;

const TabsList = styled.div`
  display: flex;
  gap: 0;
`;

const TabButton = styled.button`
  background: none;
  border: none;
  padding: 1rem 1.5rem;
  font-size: 0.95rem;
  color: ${props => (props.active ? '#00A1FF' : '#999')};
  font-weight: ${props => (props.active ? 600 : 500)};
  cursor: pointer;
  position: relative;
  transition: all 0.3s ease;

  &:hover {
    color: #0088cc;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 3px;
    background: #00A1FF;
    border-radius: 2px 2px 0 0;
    transform: ${props => (props.active ? 'scaleX(1)' : 'scaleX(0)')};
    transition: transform 0.3s ease;
    transform-origin: left;
  }
`;

const CollapsibleSection = styled.div`
  background: #f2f7fd;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  overflow: hidden;
  border: 2px solid transparent;
  transition: border-color 0.3s ease;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.8rem 2rem;
  background: ${props => (props.isOpen ? '#f2f7fd' : 'white')};
  cursor: pointer;
  border-bottom: ${props => (props.isOpen ? '1px solid #d8d8d8' : 'none')};
  transition: all 0.3s ease;

  &:hover {
    background: #f2f7fd;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

const ExpandIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
  transform: ${props => (props.isOpen ? 'rotate(0deg)' : 'rotate(-90deg)')};
  color: #404040;
  background: white;
  border-radius: 50%;
  width: 30px;
  height: 30px;
`;

const SectionContent = styled.div`
  padding: 2rem 2rem;
  display: ${props => (props.isOpen ? 'block' : 'none')};
  animation: ${props => (props.isOpen ? 'slideDown 0.3s ease' : 'none')};
  background: #f2f7fd;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const DetailLabel = styled.label`
  font-size: 0.8rem;
  color: #858B96;
  font-weight: 400;
//   text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const DetailInput = styled.input`
  padding: 0.8rem 1rem;
  border: 1px solid #E0E6F0;
  border-radius: 6px;
  background: white;
  font-size: 0.85rem;
  font-weight: 500;
  color: #1a1a1a;
  width:260px;
  height:45px;
  transition: all 0.3s ease;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #00a8ff;
    box-shadow: 0 0 0 3px rgba(0, 168, 255, 0.1);
  }

  &::placeholder {
    color: #ccc;
  }

  &:disabled {
    background: #f5f5f5;
    color: #999;
    cursor: not-allowed;
  }
`;
const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;


const CreateClaim = () => {
    const [isModalOpen, setIsModalOpen] = useState(true); // ✅ Modal opens on mount
    const [claimId, setClaimId] = useState(null);
    const [activeTab, setActiveTab] = useState('details');
    const [loading, setLoading] = useState(false);
    const { showToast } = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const basePath = process.env.PUBLIC_URL || '';
    const [submitModelOpen, setSubmitModalOpen] = useState(false);
    const navigate = useNavigate();

    const [expandedSections, setExpandedSections] = useState({
        summary: true,
        eventDetails: true,
        insuredDetails: true,
        hospitalDetails: false,
        medicalDetails: false,
        billingFinancial: false,
    });
    const hasFetchedBusinessKeys = useRef(false);

    const [entityBusinessKeys, setEntityBusinessKeys] = useState({

    })

    const [claimData, setClaimData] = useState({

    });
    const user = getUserData();

useEffect(() => {
    if (
        hasFetchedBusinessKeys.current ||
        !claimData ||
        Object.keys(claimData).length === 0
    ) return;

    hasFetchedBusinessKeys.current = true;

    const fetchBussinessKeys = async () => {
        for (let entityItem of Object.values(claimData)) {
            if (entityItem?.entityName) {
                const resp = await getEntityByFilter({
                    displayName: entityItem.entityName
                });

                const businessKeyObj = resp?.attributes?.filter(
                    (attr) => attr.is_business_key
                );

                if (businessKeyObj?.length) {
                    setEntityBusinessKeys((prev) => ({
                        ...prev,
                        [entityItem.entityName]: businessKeyObj[0].key
                    }));
                }
            }
        }
    };

    fetchBussinessKeys();
}, [claimData]);


    const handleModalSubmit = async (submittedClaimId) => {
        setLoading(true);
        const id = submittedClaimId?.trim();
        if(!id) return;
        try {
            const resp = await fetchClaimDetails(id);
            console.log("Combined Resp->", resp);
            
            if (resp) {
                setClaimId(submittedClaimId); // Update the claim ID
                setClaimData((prev) => {
                    return {
                        ...prev,
                        claimDetails: resp?.claimDetails,
                        personProfile: resp?.personProfile,
                        billingFinancial: resp?.billingFinancial,
                        dischargeSummary: resp?.dischargeSummary
                    }
                });
                showToast({
                    message: "Claim details fetched successfully.",
                    type: "success"
                });
                setIsModalOpen(false); // ✅ Close modal after successful fetch
            }
        } catch (error) {
            console.error("Error fetching claim details:", error);
            showToast({
                message: "Error fetching claim details. Please try again.",
                type: "error"
            });
            // Keep modal open on error so user can retry
        } finally {
            setLoading(false);
        }
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        // You can add navigation back or other logic here
    };

    const handleFetchDetails = async () => {
        setLoading(true);
        const id = claimId.trim();
        console.log('Trimed Id', id)
        try {
            const resp = await fetchClaimDetails(id);
            console.log("Combined Resp->", resp)
            if (resp) {
                setClaimData((prev) => {
                    return {
                        ...prev,
                        claimDetails: resp?.claimDetails,
                        personProfile: resp?.personProfile,
                        invoiceDetails: resp?.invoiceDetails,
                        dischargeSummary: resp?.dischargeSummary,
                        policyDetails: resp?.policyDetails,
                        personResume: resp?.personResume,
                        claimHistory: resp?.claimHistory
                    }
                });
                showToast({
                    message: "Case details fetched successfully.",
                    type: "success"
                });
            }
        } catch (error) {
            console.error("Error fetching details:", error);
            showToast({
                message: "Error fetching details.",
                type: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        try {
            if (!claimId.trim()) {
                showToast({
                    message: "Please enter a Claim ID",
                    type: "error"
                });
                return;
            }
            setIsSubmitting(true);
            await updateAllEntities();
            await executeWorkfolow();

            console.log('Submitting claim data:', claimData);
            showToast({
                message: `Claim ${claimId} submitted successfully!`,
                type: "success"
            });
        } catch (error) {
            console.log("Error submitting claim.",error)
        } finally {
            setIsSubmitting(false);
            setSubmitModalOpen(false);
        }
    };

    const updateAllEntities = async () => {
        let successFullCount = 0;

        for (let entityItem of Object.values(claimData)) {
            if(!entityItem) continue;
            const { created_by, updated_by, company_cd, ...remainingData } = entityItem;

            const payload = {
                "kb_code": entityItem.kb_code,
                "entity_code": entityItem.entity_name,
                "entity_data": {
                    ...remainingData
                },
                "entity_name": entityItem.entityName,
                "entity_version": 1
            }
            const resp = await updateEntityListItem(payload);
            if (resp?.status == "SUCCESS") {
                successFullCount += 1;
            }

        }

        if (successFullCount == Object.keys(claimData).length) {

            showToast({
                message: "Entities updated successfully.",
                type: 'success'
            });
        }
    }

    const saveAllEntities = async () => {
        try {
            setIsSaving(true);
            await updateAllEntities();
        } catch (error) {
            console.log("Error while saving the entities.", error);
        } finally {
            setIsSaving(false);
        }
    }


    const executeWorkfolow = async () => {
        try {
            const workflowCd = "MRUseCasePOCDetailFormatterV2";
            const userId = user?.username;
            const query = "Hi"
            const claimedAmount = claimData?.claimDetails?.billing?.grandTotal;
            const insuredName = claimData?.claimDetails?.insured?.insuredName;
            const resp = await executeWorkflowAsync(workflowCd, userId, query, claimId, claimedAmount, insuredName)
            console.log("Resp of execute workflow:", resp)
        } catch (error) {
            console.log("Error executing workflow.")
        }
    }

    const handleGoBack = () => {
        navigate('/workbench-listing');
    }

    const formatToIDR = (amount, useCode = true) => {
        if (amount === null || amount === undefined || isNaN(amount)) return "";

        const formatted = new Intl.NumberFormat("id-ID", {
            style: "currency",
            currency: "IDR",
            minimumFractionDigits: 0
        }).format(amount);

        if (useCode) {
            return formatted.replace("Rp", "IDR");
        }

        return formatted;
    };

    console.log("bussness keys", entityBusinessKeys)

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section],
        }));
    };

    const handleFieldChange = (path, value) => {
        setClaimData(prev =>
            setNestedValue(prev, path, value)
        );
    };

    const setNestedValue = (obj, path, value) => {
        const keys = path.split('.');
        let current = JSON.parse(JSON.stringify(obj));
        let temp = current;

        for (let i = 0; i < keys.length - 1; i++) {
            const key = keys[i];
            const nextKey = keys[i + 1];
            const isNextArrayIndex = /^\d+$/.test(nextKey);

            if (temp[key] === undefined) {
                temp[key] = isNextArrayIndex ? [] : {};
            }

            if (Array.isArray(temp[key])) {
                if (isNextArrayIndex) {
                    const arrayIndex = parseInt(nextKey, 10);

                    if (temp[key][arrayIndex] === undefined) {
                        temp[key][arrayIndex] = {};
                    }

                    temp = temp[key][arrayIndex];
                    i++;
                } else {
                    temp = temp[key];
                }
            } else {
                temp = temp[key];
            }
        }

        const lastKey = keys[keys.length - 1];

        if (/^\d+$/.test(lastKey)) {
            const index = parseInt(lastKey, 10);
            temp[index] = value;
        } else {
            temp[lastKey] = value;
        }

        return current;
    };

    const normalizeDate = (input) => {
        if (!input) return "";

        try {
            let date = new Date(input);

            if (isNaN(date.getTime())) {
                const months = {
                    january: 0, february: 1, march: 2, april: 3,
                    may: 4, june: 5, july: 6, august: 7,
                    september: 8, october: 9, november: 10, december: 11
                };

                const parts = input.toLowerCase().replace(/,/g, "").split(" ");

                if (parts.length === 3) {
                    let [day, month, year] = parts;

                    day = parseInt(day, 10);
                    year = parseInt(year, 10);

                    const monthIndex = months[month];

                    if (!isNaN(day) && monthIndex !== undefined && !isNaN(year)) {
                        date = new Date(year, monthIndex, day);
                    }
                }
            }

            if (isNaN(date.getTime())) return "";

            const yyyy = date.getUTCFullYear();
            const mm = String(date.getMonth() + 1).padStart(2, "0");
            const dd = String(date.getDate()).padStart(2, "0");

            return `${yyyy}-${mm}-${dd}`;
        } catch (e) {
            return "";
        }
    };

    console.log("Claim Data :", claimData)

    return (
        <PageWrapper>
            <ClaimFetchModal
                isOpen={isModalOpen}
                onClose={handleModalClose}
                onSubmit={handleModalSubmit}
                initialClaimID={claimId}
            />

              <ClaimSubmitModal
                isOpen={submitModelOpen}
                onClose={() => setSubmitModalOpen(false)}
                onSubmit={() => handleSubmit()}
            />


            <MainContent>
                {/* HEADER */}
                <Header>
                    <BackButton onClick={handleGoBack}><ChevronLeft /></BackButton>
                    <PageTitle>Create Claim</PageTitle>
                </Header>

                {/* TOP CARD */}

                <TopCard>
                    <TopContentWrapper>
                        <InputSection>
                            <img
                                src={`${basePath}/assets/images/workbench/data-pad-gray.png`}
                                alt="star-icon"
                                style={{ width: '54px', height: '54px' }}
                            />

                            <FormGroup>
                                <Label>Claim ID</Label>
                                <Input
                                    type="text"
                                    placeholder="Enter claim Id"
                                    value={claimId}
                                    onChange={(e) => setClaimId(e.target.value)}
                                />
                            </FormGroup>

                            <FetchButton onClick={handleFetchDetails} disabled={loading}>
                                {loading ? 'Loading...' : 'Fetch Details'}
                            </FetchButton>
                        </InputSection>

                        <InfoSection>
                            <InfoColumn>
                                <InfoLabel>Claimant Name</InfoLabel>
                                <InfoValue>{claimData?.claimDetails?.claimantName || "\u00A0"}</InfoValue>
                            </InfoColumn>

                            <InfoColumn>
                                <InfoLabel>Claimed Amount</InfoLabel>
                                <InfoValue>{formatToIDR(claimData?.claimDetails?.billing?.grandTotal) || 0}</InfoValue>
                            </InfoColumn>

                            <ProgressContainer>
                                <div style={{ display: 'flex', gap: '14px' }}>
                                    <InfoLabel>Policy Coverage</InfoLabel>
                                    <InfoValue>{claimData?.claimant?.coverage || 80}%</InfoValue>
                                </div>
                                <ProgressBar progress={claimData?.claimant?.coverage || 80} />
                            </ProgressContainer>
                        </InfoSection>
                    </TopContentWrapper>

                    <ButtonSection>
                        <SaveButton onClick={() => saveAllEntities()} disabled={isSaving}>
                            {isSaving ? "Saving..." : "Save"}
                        </SaveButton>

                        <SubmitButton onClick={() => setSubmitModalOpen(true)} disabled={isSubmitting}>
                            {isSubmitting ? "Submitting..." : "Submit"}
                        </SubmitButton>
                    </ButtonSection>
                </TopCard>
                <MainTabContainerHead>
                    {/* TABS */}
                    <TabsContainer>
                        <TabsList>
                            <TabButton
                                active={activeTab === 'details'}
                                onClick={() => setActiveTab('details')}
                            >
                                Claim Details
                            </TabButton>
                            <TabButton
                                active={activeTab === 'history'}
                                onClick={() => setActiveTab('history')}
                            >
                                Claim History
                            </TabButton>
                            <TabButton
                                active={activeTab === 'discharge'}
                                onClick={() => setActiveTab('discharge')}
                            >
                            Discharge Summary
                        </TabButton>
                        <TabButton
                            active={activeTab === 'policy'}
                            onClick={() => setActiveTab('policy')}
                        >
                            Policy Details
                        </TabButton>
                    </TabsList>
                </TabsContainer>

                {/* CONTENT SECTIONS */}
                {activeTab === 'details' && (
                    <>
                        {/* CLAIM SUMMARY */}
                        <CollapsibleSection isActive={expandedSections.summary}>
                            <SectionHeader
                                isOpen={expandedSections.summary}
                                onClick={() => toggleSection('summary')}
                            >
                                <SectionTitle>Claim Summary</SectionTitle>
                                <ExpandIcon isOpen={expandedSections.summary}>
                                    <ChevronDown size={20} />
                                </ExpandIcon>
                            </SectionHeader>
                            <SectionContent isOpen={expandedSections.summary}>
                                <DetailGrid>
                                    <DetailItem>
                                        <DetailLabel>Claim ID</DetailLabel>
                                        <DetailInput
                                            type="text"
                                            value={claimData?.claimDetails?.caseId}
                                            onChange={(e) => handleFieldChange('claimDetails.caseId', e.target.value)}
                                            placeholder="C-874905"
                                        />
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>Claim Type</DetailLabel>
                                        <DetailInput
                                            type="text"
                                            value={claimData?.claimDetails?.claimType}
                                            onChange={(e) => handleFieldChange('claimDetails.claimType', e.target.value)}
                                            placeholder="Hospitalization"
                                        />
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>Claim Status</DetailLabel>
                                        <DetailInput
                                            type="text"
                                            value={claimData?.claimDetails?.claimStatusText}
                                            onChange={(e) => handleFieldChange('claimDetails.claimStatusText', e.target.value)}
                                            placeholder="Under Assessment"
                                        />
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>Decision Status</DetailLabel>
                                        <DetailInput
                                            type="text"
                                            value={claimData?.claimDetails?.decisionStatusText}
                                            onChange={(e) => handleFieldChange('claimDetails.decisionStatusText', e.target.value)}
                                            placeholder="Pending"
                                        />
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>Decision Reason</DetailLabel>
                                        <DetailInput
                                            type="text"
                                            value={claimData?.claimDetails?.decisionReason}
                                            onChange={(e) => handleFieldChange('claimDetails.decisionReason', e.target.value)}
                                            placeholder="Pending decision here"
                                        />
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>Claim Mode</DetailLabel>
                                        <DetailInput
                                            type="text"
                                            value={claimData?.claimDetails?.claimMode}
                                            onChange={(e) => handleFieldChange('claimDetails.claimMode', e.target.value)}
                                            placeholder="Cashless"
                                        />
                                    </DetailItem>
                                </DetailGrid>
                            </SectionContent>
                        </CollapsibleSection>

                        {/* EVENT DETAILS */}
                        <CollapsibleSection isActive={expandedSections.eventDetails}>
                            <SectionHeader
                                isOpen={expandedSections.eventDetails}
                                onClick={() => toggleSection('eventDetails')}
                            >
                                <SectionTitle>Event Details</SectionTitle>
                                <ExpandIcon isOpen={expandedSections.eventDetails}>
                                    <ChevronDown size={20} />
                                </ExpandIcon>
                            </SectionHeader>
                            <SectionContent isOpen={expandedSections.eventDetails}>
                                <DetailGrid>
                                    <DetailItem>
                                        <DetailLabel>Event Type</DetailLabel>
                                        <DetailInput
                                            type="text"
                                            value={claimData?.claimDetails?.event?.eventType}
                                            onChange={(e) => handleFieldChange('claimDetails.event.eventType', e.target.value)}
                                            placeholder="Medical"
                                        />
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>Cause Type</DetailLabel>
                                        <DetailInput
                                            type="text"
                                            value={claimData?.claimDetails?.event?.causeType}
                                            onChange={(e) => handleFieldChange('claimDetails.event.causeType', e.target.value)}
                                        />
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>Admission Date</DetailLabel>
                                        <DetailInput
                                            type="date"
                                            value={normalizeDate(claimData?.claimDetails?.event?.admissionDate)}
                                            onChange={(e) => handleFieldChange('claimDetails.event.admissionDate', e.target.value)}
                                        />
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>Discharge Date</DetailLabel>
                                        <DetailInput
                                            type="date"
                                            value={normalizeDate(claimData?.claimDetails?.event?.dischargeDate)}
                                            onChange={(e) => handleFieldChange('claimDetails.event.dischargeDate', e.target.value)}
                                            placeholder="Non-Accidental"
                                        />
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>Intimation Date</DetailLabel>
                                        <DetailInput
                                            type="date"
                                            value={normalizeDate(claimData?.claimDetails?.event?.intimationDateTime)}
                                            onChange={(e) => handleFieldChange('claimDetails.event.intimationDateTime', e.target.value)}
                                            placeholder="Passed dummy text here"
                                        />
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>Length of Stay</DetailLabel>
                                        <DetailInput
                                            type="text"
                                            value={claimData?.claimDetails?.event?.lengthOfStayDays}
                                            onChange={(e) => handleFieldChange('claimDetails.event.lengthOfStayDays', e.target.value)}
                                            placeholder="4 days"
                                        />
                                    </DetailItem>
                                </DetailGrid>
                            </SectionContent>
                        </CollapsibleSection>

                        {/* INSURED DETAILS */}
                        <CollapsibleSection isActive={expandedSections.insuredDetails}>
                            <SectionHeader
                                isOpen={expandedSections.insuredDetails}
                                onClick={() => toggleSection('insuredDetails')}
                            >
                                <SectionTitle>Insured Details</SectionTitle>
                                <ExpandIcon isOpen={expandedSections.insuredDetails}>
                                    <ChevronDown size={20} />
                                </ExpandIcon>
                            </SectionHeader>
                            <SectionContent isOpen={expandedSections.insuredDetails}>
                                <DetailGrid>
                                    <DetailItem>
                                        <DetailLabel>Insurer Name</DetailLabel>
                                        <DetailInput
                                            type="text"
                                            value={claimData?.claimDetails?.insured?.insuredName}
                                            onChange={(e) => handleFieldChange('claimDetails.insured.insuredName', e.target.value)}
                                            placeholder="Repair Machen"
                                        />
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>Insurance ID</DetailLabel>
                                        <DetailInput
                                            type="text"
                                            value={claimData?.claimDetails?.insured?.insuredId}
                                            onChange={(e) => handleFieldChange('claimDetails.insured.insuredId', e.target.value)}
                                            placeholder="INS-2874040"
                                        />
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>Date of Birth</DetailLabel>
                                        <DetailInput
                                            type="date"
                                            value={normalizeDate(claimData?.claimDetails?.insured?.insuredDateOfBirth)}
                                            onChange={(e) => handleFieldChange('claimDetails.insured.insuredDateOfBirth', e.target.value)}
                                        />
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>Gender</DetailLabel>
                                        <DetailInput
                                            type="text"
                                            value={claimData?.claimDetails?.insured?.gender}
                                            onChange={(e) => handleFieldChange('claimDetails.insured.gender', e.target.value)}
                                            placeholder="Male"
                                        />
                                    </DetailItem>
                                </DetailGrid>
                            </SectionContent>
                        </CollapsibleSection>

                        {/* HOSPITAL DETAILS */}
                        <CollapsibleSection isActive={expandedSections.hospitalDetails}>
                            <SectionHeader
                                isOpen={expandedSections.hospitalDetails}
                                onClick={() => toggleSection('hospitalDetails')}
                            >
                                <SectionTitle>Hospital Details</SectionTitle>
                                <ExpandIcon isOpen={expandedSections.hospitalDetails}>
                                    <ChevronDown size={20} />
                                </ExpandIcon>
                            </SectionHeader>
                            <SectionContent isOpen={expandedSections.hospitalDetails}>
                                <DetailGrid>
                                    <DetailItem>
                                        <DetailLabel>Hospital Name</DetailLabel>
                                        <DetailInput
                                            type="text"
                                            value={claimData?.claimDetails?.hospital?.hospitalName}
                                            onChange={(e) => handleFieldChange('claimDetails.hospital.hospitalName', e.target.value)}
                                            placeholder="Apollo Hospital"
                                        />
                                    </DetailItem>
                                </DetailGrid>
                            </SectionContent>
                        </CollapsibleSection>

                        {/* MEDICAL DETAILS */}
                        <CollapsibleSection isActive={expandedSections.medicalDetails}>
                            <SectionHeader
                                isOpen={expandedSections.medicalDetails}
                                onClick={() => toggleSection('medicalDetails')}
                            >
                                <SectionTitle>Medical Details</SectionTitle>
                                <ExpandIcon isOpen={expandedSections.medicalDetails}>
                                    <ChevronDown size={20} />
                                </ExpandIcon>
                            </SectionHeader>
                            <SectionContent isOpen={expandedSections.medicalDetails}>
                                <DetailGrid>
                                    <DetailItem>
                                        <DetailLabel>Primary Diagnosis</DetailLabel>
                                        <DetailInput
                                            type="text"
                                            value={claimData?.claimDetails?.medical?.diagnosis}
                                            onChange={(e) => handleFieldChange('claimDetails.medical.diagnosis', e.target.value)}
                                            placeholder="Acute Appendicitis"
                                        />
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>Primary Diagnosis Code</DetailLabel>
                                        <DetailInput
                                            type="text"
                                            value={claimData?.claimDetails?.medical?.diagnosis}
                                            onChange={(e) => handleFieldChange('claimDetails.medical.diagnosis', e.target.value)}
                                            placeholder="K35.80"
                                        />
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>Medical Procedure</DetailLabel>
                                        <DetailInput
                                            type="text"
                                            value={claimData?.claimDetails?.medical?.medicalProcedure}
                                            onChange={(e) => handleFieldChange('claimDetails.medical.medicalProcedure', e.target.value)}
                                            placeholder="peritonsillar"
                                        />
                                    </DetailItem>
                                </DetailGrid>
                            </SectionContent>
                        </CollapsibleSection>

                        {/* BILLING & FINANCIAL SUMMARY */}
                        <CollapsibleSection isActive={expandedSections.billingFinancial}>
                            <SectionHeader
                                isOpen={expandedSections.billingFinancial}
                                onClick={() => toggleSection('billingFinancial')}
                            >
                                <SectionTitle>Billing & Financial Summary</SectionTitle>
                                <ExpandIcon isOpen={expandedSections.billingFinancial}>
                                    <ChevronDown size={20} />
                                </ExpandIcon>
                            </SectionHeader>
                            <SectionContent isOpen={expandedSections.billingFinancial}>
                                <DetailGrid>
                                    <DetailItem>
                                        <DetailLabel>Claimed Amount</DetailLabel>
                                        <DetailInput
                                            type="number"
                                            value={claimData?.claimDetails?.billing?.grandTotal}
                                            onChange={(e) => handleFieldChange('claimDetails.billing.grandTotal', Number(e.target.value))}
                                            placeholder="24,48,000"
                                        />
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>Approved Amount</DetailLabel>
                                        <DetailInput
                                            type="number"
                                            value={claimData?.claimDetails?.billing?.approvedAmount}
                                            onChange={(e) => handleFieldChange('claimDetails.billing.approvedAmount', Number(e.target.value))}
                                            placeholder="6,63,600"
                                        />
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>Policy Payable Amount</DetailLabel>
                                        <DetailInput
                                            type="number"
                                            value={claimData?.claimDetails?.billing?.policyPayableAmount}
                                            onChange={(e) => handleFieldChange('claimDetails.billing.policyPayableAmount', Number(e.target.value))}
                                            placeholder="1,28,460"
                                        />
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>Total Net Amount</DetailLabel>
                                        <DetailInput
                                            type="number"
                                            value={claimData?.claimDetails?.billing?.totalNetAmount}
                                            onChange={(e) => handleFieldChange('claimDetails.billing.totalNetAmount', Number(e.target.value))}
                                            placeholder="N/A"
                                        />
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>Disapproved Amount</DetailLabel>
                                        <DetailInput
                                            type="number"
                                            value={claimData?.claimDetails?.billing?.disapprovedAmount}
                                            onChange={(e) => handleFieldChange('claimDetails.billing.disapprovedAmount', Number(e.target.value || 0))}
                                            placeholder="5,25,000"
                                        />
                                    </DetailItem>
                                </DetailGrid>
                            </SectionContent>
                        </CollapsibleSection>
                    </>
                )}

                {/* OTHER TABS CONTENT */}
                {activeTab === 'history' && (
                    <CollapsibleSection>
                        <SectionHeader isOpen={true}>
                            <SectionTitle>Claim History</SectionTitle>
                        </SectionHeader>
                        <SectionContent isOpen={true}>
                            <DetailGrid>
                                <DetailItem>
                                    <DetailLabel>Created Date</DetailLabel>
                                    <DetailInput type="date" defaultValue="2024-03-15" />
                                </DetailItem>
                                <DetailItem>
                                    <DetailLabel>Last Updated</DetailLabel>
                                    <DetailInput type="date" defaultValue="2024-03-22" />
                                </DetailItem>
                                <DetailItem>
                                    <DetailLabel>Days Pending</DetailLabel>
                                    <DetailInput type="text" defaultValue="7 Days" />
                                </DetailItem>
                            </DetailGrid>
                        </SectionContent>
                    </CollapsibleSection>
                )}

                {activeTab === 'discharge' && (
                    <>
                        <CollapsibleSection>
                            <SectionHeader isOpen={true}>
                                <SectionTitle>Patient Information</SectionTitle>
                            </SectionHeader>
                            <SectionContent isOpen={true}>
                                <DetailGrid>
                                    <DetailItem>
                                        <DetailLabel>Patient Name</DetailLabel>
                                        <DetailInput
                                            type="text"
                                            value={claimData?.dischargeSummary?.patient?.name}
                                            onChange={(e) => handleFieldChange('dischargeSummary.patient.name', e.target.value)}
                                            placeholder="John Doe" />
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>Patient DOB</DetailLabel>
                                        <DetailInput type="date"
                                            value={normalizeDate(claimData?.dischargeSummary?.patient?.dateOfBirth)}
                                            onChange={(e) => handleFieldChange('dischargeSummary.patient.dateOfBirth', e.target.value)}
                                            placeholder="13-06-1997" />
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>Patient Age</DetailLabel>
                                        <DetailInput type="number"
                                            value={claimData?.dischargeSummary?.patient?.age}
                                            onChange={(e) => handleFieldChange('dischargeSummary.patient.age', Number(e.target.value))}
                                            placeholder="32" />
                                    </DetailItem>
                                </DetailGrid>
                            </SectionContent>
                        </CollapsibleSection>

                        <CollapsibleSection>
                            <SectionHeader isOpen={true}>
                                <SectionTitle>Hospital Information</SectionTitle>
                            </SectionHeader>
                            <SectionContent isOpen={true}>
                                <DetailGrid>
                                    <DetailItem>
                                        <DetailLabel>Hospital Name</DetailLabel>
                                        <DetailInput
                                            type="text"
                                            value={claimData?.dischargeSummary?.hospital?.hospitalName}
                                            onChange={(e) => handleFieldChange('dischargeSummary.hospital.hospitalName', e.target.value)}
                                            placeholder="City genaral" />
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>Doctor Name</DetailLabel>
                                        <DetailInput type="text"
                                            value={claimData?.dischargeSummary?.hospital?.doctorName}
                                            onChange={(e) => handleFieldChange('dischargeSummary.hospital.doctorName', e.target.value)}
                                            placeholder="Dr. cen" />
                                    </DetailItem>

                                    <DetailItem>
                                        <DetailLabel>Attending Physician</DetailLabel>
                                        <DetailInput type="text"
                                            value={claimData?.dischargeSummary?.hospital?.attendingPhysician}
                                            onChange={(e) => handleFieldChange('dischargeSummary.hospital.attendingPhysician', e.target.value)}
                                            placeholder="Dr. larry" />
                                    </DetailItem>
                                </DetailGrid>
                            </SectionContent>
                        </CollapsibleSection>

                        <CollapsibleSection>
                            <SectionHeader isOpen={true}>
                                <SectionTitle>Admission Details</SectionTitle>
                            </SectionHeader>
                            <SectionContent isOpen={true}>
                                <DetailGrid>
                                    <DetailItem>
                                        <DetailLabel>Admission Date</DetailLabel>
                                        <DetailInput
                                            type="date"
                                            value={normalizeDate(claimData?.dischargeSummary?.admission?.admissionDate)}
                                            onChange={(e) => handleFieldChange('dischargeSummary.admission.admissionDate', e.target.value)}
                                            placeholder="23/10/2024" />
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>Discharge Date</DetailLabel>
                                        <DetailInput type="date"
                                            value={normalizeDate(claimData?.dischargeSummary?.admission?.dischargeDate)}
                                            onChange={(e) => handleFieldChange('dischargeSummary.admission.dischargeDate', e.target.value)}
                                            placeholder="30/10/2024" />
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>Length of Stay</DetailLabel>
                                        <DetailInput type="number"
                                            value={claimData?.dischargeSummary?.admission?.lengthOfStayDays}
                                            onChange={(e) => handleFieldChange('dischargeSummary.admission.lengthOfStayDays', Number(e.target.value))}
                                            placeholder="4" />
                                    </DetailItem>
                                </DetailGrid>
                            </SectionContent>
                        </CollapsibleSection>

                        <CollapsibleSection>
                            <SectionHeader isOpen={true}>
                                <SectionTitle>Diagnosis Details</SectionTitle>
                            </SectionHeader>
                            <SectionContent isOpen={true}>
                                <DetailGrid>
                                    <DetailItem>
                                        <DetailLabel>Primary Diagnosis</DetailLabel>
                                        <DetailInput
                                            type="text"
                                            value={claimData?.dischargeSummary?.diagnosis?.primaryDiagnosisText}
                                            onChange={(e) => handleFieldChange('dischargeSummary.diagnosis.primaryDiagnosisText', e.target.value)}
                                            placeholder="Accute Appendics" />
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>ICD Code</DetailLabel>
                                        <DetailInput type="text"
                                            value={claimData?.dischargeSummary?.diagnosis?.primaryDiagnosis?.code}
                                            onChange={(e) => handleFieldChange('dischargeSummary.diagnosis.primaryDiagnosis.code', e.target.value)}
                                            placeholder="K35.80" />
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>Other Diagnosis</DetailLabel>
                                        <DetailInput type="text"
                                            value={claimData?.dischargeSummary?.diagnosis?.otherDiagnosis}
                                            onChange={(e) => handleFieldChange('dischargeSummary.diagnosis.otherDiagnosis', e.target.value)}
                                            placeholder="Nil" />
                                    </DetailItem>
                                </DetailGrid>
                            </SectionContent>
                        </CollapsibleSection>

                        <CollapsibleSection>
                            <SectionHeader isOpen={true}>
                                <SectionTitle>Treatment and Procedure</SectionTitle>
                            </SectionHeader>
                            <SectionContent isOpen={true}>
                                <DetailGrid>
                                    <DetailItem>
                                        <DetailLabel>Precedure Name</DetailLabel>
                                        <DetailInput
                                            type="text"
                                            value={claimData?.dischargeSummary?.treatment?.medicalProcedure}
                                            onChange={(e) => handleFieldChange('dischargeSummary.treatment.medicalProcedure', e.target.value)}
                                            placeholder="Laproscopic Appendectomy" />
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>Date of Procedure</DetailLabel>
                                        <DetailInput type="date"
                                            value={normalizeDate(claimData?.dischargeSummary?.treatment?.procedures?.[0]?.date)}
                                            onChange={(e) => handleFieldChange('dischargeSummary.treatment.procedures[0].date', e.target.value)}
                                            placeholder="18/10/2025" />
                                    </DetailItem>
                                </DetailGrid>
                            </SectionContent>
                        </CollapsibleSection>
                    </>
                )}

                {activeTab === 'policy' && (
                    <>
                    <CollapsibleSection>
                        <SectionHeader isOpen={true}>
                            <SectionTitle>Policy Identifires</SectionTitle>
                        </SectionHeader>
                        <SectionContent isOpen={true}>
                            <DetailGrid>
                                <DetailItem>
                                    <DetailLabel>Policy Number</DetailLabel>
                                    <DetailInput 
                                        type="text" 
                                        placeholder="POL-2024-001234"
                                        value={claimData?.policyDetails?.policyNumber}
                                        onChange={(e) => handleFieldChange('policyDetails.policyNumber', e.target.value)} />
                                </DetailItem>
                                <DetailItem>
                                    <DetailLabel>Case Id</DetailLabel>
                                    <DetailInput 
                                        type="text" 
                                        placeholder="MBMAACP-55_TC001"
                                        value={claimData?.policyDetails?.caseId}
                                        onChange={(e) => handleFieldChange('policyDetails.caseId', e.target.value)} />
                                </DetailItem>
                                <DetailItem>
                                    <DetailLabel>Policy Owner ID</DetailLabel>
                                    <DetailInput 
                                        type="text" 
                                        placeholder="PO-ALLFAIL-0001"
                                        value={claimData?.policyDetails?.personProfileId}
                                        onChange={(e) => handleFieldChange('policyDetails.personProfileId', e.target.value)} />
                                </DetailItem>

                                 <DetailItem>
                                    <DetailLabel>Policy Owner Name</DetailLabel>
                                    <DetailInput 
                                        type="text" 
                                        placeholder="Rani merry"
                                        value={claimData?.policyDetails?.policyOwnerName}
                                        onChange={(e) => handleFieldChange('policyDetails.policyOwnerName', e.target.value)}  />
                                </DetailItem>
                            </DetailGrid>
                        </SectionContent>
                    </CollapsibleSection>

                    <CollapsibleSection>
                        <SectionHeader isOpen={true}>
                            <SectionTitle>Policy Information</SectionTitle>
                        </SectionHeader>
                        <SectionContent isOpen={true}>
                            <DetailGrid>
                                <DetailItem>
                                    <DetailLabel>Policy Expiry Date</DetailLabel>
                                    <DetailInput 
                                        type="date" 
                                        placeholder="26/10/2025"
                                        value={normalizeDate(claimData?.policyDetails?.policy?.expiryDate)}
                                        onChange={(e) => handleFieldChange('policyDetails.policy.expiryDate', e.target.value)} />
                                </DetailItem>
                                <DetailItem>
                                    <DetailLabel>Insurance Type</DetailLabel>
                                    <DetailInput 
                                        type="text" 
                                        placeholder="Hospital"
                                        value={claimData?.policyDetails?.policy?.insuranceType}
                                        onChange={(e) => handleFieldChange('policyDetails.policy.insuranceType', e.target.value)} />
                                </DetailItem>
                                <DetailItem>
                                    <DetailLabel>Line Of Business</DetailLabel>
                                    <DetailInput 
                                        type="text" 
                                        placeholder="Induvisual"
                                        value={claimData?.policyDetails?.policy?.lineOfBusiness}
                                        onChange={(e) => handleFieldChange('policyDetails.policy.lineOfBusiness', e.target.value)} />
                                </DetailItem>

                                 <DetailItem>
                                    <DetailLabel>Paid To Date</DetailLabel>
                                    <DetailInput 
                                        type="date" 
                                        placeholder="10/10/2024"
                                        value={normalizeDate(claimData?.policyDetails?.policy?.paidToDate)}
                                        onChange={(e) => handleFieldChange('policyDetails.policy.paidToDate', e.target.value)}  />
                                </DetailItem>

                                 <DetailItem>
                                    <DetailLabel>Payment Mode</DetailLabel>
                                    <DetailInput 
                                        type="text" 
                                        placeholder="Annually"
                                        value={claimData?.policyDetails?.policy?.paymentMode}
                                        onChange={(e) => handleFieldChange('policyDetails.policy.paymentMode', e.target.value)}  />
                                </DetailItem>

                                 <DetailItem>
                                    <DetailLabel>Policy Currency</DetailLabel>
                                    <DetailInput 
                                        type="text" 
                                        placeholder="IDR"
                                        value={claimData?.policyDetails?.policy?.policyCurrency}
                                        onChange={(e) => handleFieldChange('policyDetails.policy.policyCurrency', e.target.value)}  />
                                </DetailItem>

                                 <DetailItem>
                                    <DetailLabel>Policy Status</DetailLabel>
                                    <DetailInput 
                                        type="text" 
                                        placeholder=""
                                        value={claimData?.policyDetails?.policy?.policyStatus}
                                        onChange={(e) => handleFieldChange('policyDetails.policy.policyStatus', e.target.value)}  />
                                </DetailItem>
                            </DetailGrid>
                        </SectionContent>
                    </CollapsibleSection>
                    </>
                )}
            </MainTabContainerHead>
            </MainContent>
        </PageWrapper>
    );
};

export default CreateClaim;